import { createEnterKeyHandler } from 'utils/form'
import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { BOOKING_STATUS_OPTIONS } from 'static/constants_opcalendar'
import { InputText, CheckBox, InputRadio, Button } from 'components/atoms'

export interface IOtaReservationFilterOptions {
  missing_jwt_post_id?: boolean
  missing_jwt_tour_id?: boolean
  missing_jwt_tour_group_id?: boolean
  jwt_post_id?: string
  jwt_tour_id?: string
  seller_booking_id?: string
  channel_manager_booking_id?: string
  channel_manager_confirmation_code?: string
  customer_email?: string
  tour_product_id?: string
  seller?: string
  booking_status_id?: string
  tour_date?: string
  seller_booking_created_at?: string
  canceled_at?: string
}

interface IProps {
  filterOptions: IOtaReservationFilterOptions
  setFilterOptions: React.Dispatch<React.SetStateAction<IOtaReservationFilterOptions>>
  setShowFilter: React.Dispatch<React.SetStateAction<any>>
}

const SELLER_OPTIONS = [
  { id: 'airbnb', label: 'Airbnb', value: 'airbnb' },
  { id: 'byfood', label: 'Byfood', value: 'byfood' },
  { id: 'getyourguide', label: 'GetYourGuide', value: 'getyourguide' },
  { id: 'headout', label: 'Headout', value: 'headout' },
  { id: 'jwt', label: 'JWT', value: 'jwt' },
  { id: 'klook', label: 'Klook', value: 'klook' },
  { id: 'project_expedition', label: 'Project Expedition', value: 'project_expedition' },
  { id: 'trip_to_japan', label: 'Trip to Japan', value: 'trip_to_japan' },
  { id: 'viator', label: 'Viator', value: 'viator' },
  { id: 'direct', label: 'BokunDirect', value: 'direct' },
  { id: 'agent', label: 'BokunAgent', value: 'agent' },
]

const OtaReservationFilter: React.VFC<IProps> = ({
  filterOptions,
  setFilterOptions,
  setShowFilter,
}) => {
  const [missingJwtPostId, setMissingJwtPostId] = React.useState(
    filterOptions.missing_jwt_post_id || false
  )
  const [missingJwtTourId, setMissingJwtTourId] = React.useState(
    filterOptions.missing_jwt_tour_id || false
  )
  const [missingJwtTourGroupId, setMissingJwtTourGroupId] = React.useState(
    filterOptions.missing_jwt_tour_group_id || false
  )

  const [jwtPostId, setJwtPostId] = React.useState(filterOptions.jwt_post_id || '')
  const [jwtTourId, setJwtTourId] = React.useState(filterOptions.jwt_tour_id || '')
  const [sellerBookingId, setSellerBookingId] = React.useState(
    filterOptions.seller_booking_id || ''
  )
  const [channelManagerBookingId, setChannelManagerBookingId] = React.useState(
    filterOptions.channel_manager_booking_id || ''
  )
  const [channelManagerConfirmationCode, setChannelManagerConfirmationCode] = React.useState(
    filterOptions.channel_manager_confirmation_code || ''
  )
  const [customerEmail, setCustomerEmail] = React.useState(filterOptions.customer_email || '')
  const [tourProductId, setTourProductId] = React.useState(filterOptions.tour_product_id || '')

  const [seller, setSeller] = React.useState(filterOptions.seller || '')
  const [bookingStatusId, setBookingStatusId] = React.useState(
    filterOptions.booking_status_id || ''
  )

  const [sellerBookingCreatedAt, setSellerBookingCreatedAt] = React.useState(
    filterOptions.seller_booking_created_at || ''
  )
  const [canceledAt, setCanceledAt] = React.useState(filterOptions.canceled_at || '')

  const handleConfirm = React.useCallback(() => {
    setFilterOptions(currentOptions => ({
      ...currentOptions,
      missing_jwt_post_id: missingJwtPostId,
      missing_jwt_tour_id: missingJwtTourId,
      missing_jwt_tour_group_id: missingJwtTourGroupId,
      jwt_post_id: jwtPostId,
      jwt_tour_id: jwtTourId,
      seller_booking_id: sellerBookingId,
      channel_manager_booking_id: channelManagerBookingId,
      channel_manager_confirmation_code: channelManagerConfirmationCode,
      customer_email: customerEmail,
      tour_product_id: tourProductId,
      seller,
      booking_status_id: bookingStatusId,
      seller_booking_created_at: sellerBookingCreatedAt,
      canceled_at: canceledAt,
    }))
    setShowFilter(false)
  }, [
    missingJwtPostId,
    missingJwtTourId,
    missingJwtTourGroupId,
    jwtPostId,
    jwtTourId,
    sellerBookingId,
    channelManagerBookingId,
    channelManagerConfirmationCode,
    customerEmail,
    tourProductId,
    seller,
    bookingStatusId,
    sellerBookingCreatedAt,
    canceledAt,
    setFilterOptions,
    setShowFilter,
  ])

  const handleSearchKeyDown = createEnterKeyHandler(handleConfirm)

  return (
    <Wrapper>
      <div className="scroller">
        <div className="filterCategory">
          <div className="type">欠損データ検索</div>
          <div className="options gapped">
            <CheckBox
              name="missing_jwt_post_id"
              value="true"
              label="ツアー情報(jwt_post_id) 一致なし"
              showLabelInline={true}
              onChangeHandler={e => setMissingJwtPostId(e.target.checked)}
              checked={missingJwtPostId}
            />
            <CheckBox
              name="missing_jwt_tour_id"
              value="true"
              label="ツアー日時(jwt_tour_id) 一致なし"
              showLabelInline={true}
              onChangeHandler={e => setMissingJwtTourId(e.target.checked)}
              checked={missingJwtTourId}
            />
            <CheckBox
              name="missing_jwt_tour_group_id"
              value="true"
              label="ツアーグループ(jwt_tour_group_id) なし"
              showLabelInline={true}
              onChangeHandler={e => setMissingJwtTourGroupId(e.target.checked)}
              checked={missingJwtTourGroupId}
            />
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">完全一致検索</div>
          <div className="textInputs">
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="jwt_post_id"
              value={jwtPostId}
              onChangeHandler={e => setJwtPostId(e.target.value)}
              placeholder="jwt_post_id"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="jwt_tour_id"
              value={jwtTourId}
              onChangeHandler={e => setJwtTourId(e.target.value)}
              placeholder="jwt_tour_id"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="seller_booking_id"
              value={sellerBookingId}
              onChangeHandler={e => setSellerBookingId(e.target.value)}
              placeholder="OTA予約ID (e.g. RE-...)"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="channel_manager_booking_id"
              value={channelManagerBookingId}
              onChangeHandler={e => setChannelManagerBookingId(e.target.value)}
              placeholder="Bokun ID (e.g. 85683461)"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="channel_manager_confirmation_code"
              value={channelManagerConfirmationCode}
              onChangeHandler={e => setChannelManagerConfirmationCode(e.target.value)}
              placeholder="Booking ref. (VIA-.../TOK-...)"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="customer_email"
              value={customerEmail}
              onChangeHandler={e => setCustomerEmail(e.target.value)}
              placeholder="Email"
            />
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="tour_product_id"
              value={tourProductId}
              onChangeHandler={e => setTourProductId(e.target.value)}
              placeholder="商品コード (21490P...)"
            />
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">販売元</div>
          <div className="options">
            <InputRadio
              name="seller"
              onChangeHandler={e => setSeller(e.target.value)}
              options={SELLER_OPTIONS}
              defaultValue={seller}
            />
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">予約ステータス</div>
          <div className="options">
            <InputRadio
              name="booking_status_id"
              onChangeHandler={e => setBookingStatusId(e.target.value)}
              options={BOOKING_STATUS_OPTIONS}
              defaultValue={bookingStatusId}
            />
          </div>
        </div>

        <div className="filterCategory">
          <div className="type">OTA予約日</div>
          <div className="textInputs">
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="seller_booking_created_at"
              type="date"
              value={sellerBookingCreatedAt}
              onChangeHandler={e => setSellerBookingCreatedAt(e.target.value)}
              placeholder="seller_booking_created_at"
            />
          </div>
          <div className="type">キャンセル日</div>
          <div className="textInputs">
            <InputText
              onKeyDownHandler={handleSearchKeyDown}
              name="canceled_at"
              type="date"
              value={canceledAt}
              onChangeHandler={e => setCanceledAt(e.target.value)}
              placeholder="canceled_at"
            />
          </div>
        </div>
      </div>
      <div className="actions">
        <div className="action">
          <Button handleClick={() => setShowFilter(false)}>キャンセル</Button>
          <Button primary={true} handleClick={handleConfirm}>
            フィルター
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  font-size: 12px;
  background-color: ${constants.COLORS.white};
  border: solid 1px ${constants.COLORS.divider};
  border-radius: 4px;
  box-shadow: ${constants.COLORS.cardShadow};
  z-index: 6001;
  .scroller {
    max-height: calc(100dvh - 13rem);
    padding: 16px 16px 80px;
    overflow-y: auto;
  }
  .filterCategory {
    margin-bottom: 20px;
    .type {
      margin-bottom: 8px;
      color: ${constants.COLORS.subText};
      font-weight: bold;
    }
    .textInputs {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .options {
      display: flex;
      flex-wrap: wrap;
      &.gapped {
        gap: 8px 16px;
      }
      .CheckBox {
        margin: 0;
        .CheckBox_Wrapper {
          .CheckBox_Main {
            > label {
              span {
                font-size: 13px;
                font-weight: normal;
              }
            }
          }
        }
      }
      .InputRadio_Labels {
        gap: 8px 16px;
        label {
          span {
            font-size: 13px;
            font-weight: normal;
          }
        }
      }
    }
  }
  .actions {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 16px;
    background-color: ${constants.COLORS.white};
    border-top: solid 1px ${constants.COLORS.divider};
    border-radius: 0 0 4px 4px;
    .action {
      display: flex;
      align-items: center;
      justify-content: space-between;
      button {
        width: calc(50% - 8px);
      }
    }
  }
`

export default OtaReservationFilter
